import {
  Checkbox,
  Loading,
  Pagination,
  Search,
  Tab,
  Tabs,
} from 'carbon-components-react';
import './daily-fee.styles.scss';
import useSWR, { mutate } from 'swr';
import CustomButton from '../../components/custom-button/custom-btn.component';
import { useEffect, useState } from 'react';
import { useCustomMutation } from '../../utils/useCustomMutation';
import { postMethod } from '../../api/common';
import { useParams } from 'react-router-dom';
import PageLayout from '../../components/PageLayout';
import Breadcrumbs from '../../components/breadcrumbs/Breadcrumbs';
import { Printer } from 'react-feather';
import { PrintView } from '../../components/PrintView/print-view.component';
import PrintOutHead from '../../components/PrintOutHead';
import { useDebounceState } from '../../utils/useDebounceState';
import { getQueryString } from '../../utils/common.util';

export default function AddStudentsToFeeType() {
  const { feeTypeId, clsId } = useParams();

  const singleFeeTypeUrl = `daily-billing/fees/${feeTypeId}/summary`;
  const { data: d, isLoading: isFeeTyping } = useSWR(singleFeeTypeUrl);
  const feeType = d?.dailyBillingFee ?? {};

  const cls = feeType?.classAmounts?.find((c) => c.classId === Number(clsId));

  const [payingSearchTerm, setPayingSearchTerm] = useState('');
  const [debouncedPayingSearchTerm, setDebouncedPayingSearchTerm] =
    useDebounceState('');
  const [nonPayingSearchTerm, setNonPayingSearchTerm] = useState('');
  const [debouncedNonPayingSearchTerm, setDebouncedNonPayingSearchTerm] =
    useDebounceState('');

  useEffect(() => {
    setDebouncedPayingSearchTerm(payingSearchTerm);
  }, [payingSearchTerm, setDebouncedPayingSearchTerm]);
  useEffect(() => {
    setDebouncedNonPayingSearchTerm(nonPayingSearchTerm);
  }, [nonPayingSearchTerm, setDebouncedNonPayingSearchTerm]);

  const [payingPaginate, setPayingPaginate] = useState({
    page: 1,
    pageSize: 50,
  });
  const [nonPayingPaginate, setNonPayingPaginate] = useState({
    page: 1,
    pageSize: 50,
  });

  // Jump back to page 1 whenever a filter changes
  useEffect(() => {
    setPayingPaginate((p) => ({ ...p, page: 1 }));
  }, [debouncedPayingSearchTerm]);
  useEffect(() => {
    setNonPayingPaginate((p) => ({ ...p, page: 1 }));
  }, [debouncedNonPayingSearchTerm]);

  // Students of this class assigned to the fee (server-side: Enrolled only, paged, searchable).
  const payingStudentsUrl = feeType?.id
    ? `daily-billing/fees/assigned-students?${getQueryString({
        dailyBillingFeeId: feeType.id,
        classId: clsId,
        assignedToDailyBillingFee: true,
        query: debouncedPayingSearchTerm,
        skip: payingPaginate.page - 1,
        take: payingPaginate.pageSize,
      })}`
    : null;
  const { data: payingData, isLoading: isPayingLoading } =
    useSWR(payingStudentsUrl);
  const payingStudents = payingData?.response ?? [];
  const payingTotal = payingData?.paginationInfo?.totalCount ?? 0;

  // Students of this class NOT assigned to the fee (server-side, paged).
  const nonPayingStudentsUrl = feeType?.id
    ? `daily-billing/fees/assigned-students?${getQueryString({
        dailyBillingFeeId: feeType.id,
        classId: clsId,
        assignedToDailyBillingFee: false,
        query: debouncedNonPayingSearchTerm,
        skip: nonPayingPaginate.page - 1,
        take: nonPayingPaginate.pageSize,
      })}`
    : null;
  const { data: nonPayingData, isLoading: isClassing } =
    useSWR(nonPayingStudentsUrl);
  const nonPayingStudents = nonPayingData?.response ?? [];
  const nonPayingTotal = nonPayingData?.paginationInfo?.totalCount ?? 0;

  const isLoading = isFeeTyping || isClassing;

  const [studentsToAdd, setStudentsToAdd] = useState([]);
  const [studentsToRemove, setStudentsToRemove] = useState([]);

  function selectStudentToAdd(studentId) {
    if (studentsToAdd.includes(studentId)) {
      setStudentsToAdd(studentsToAdd.filter((id) => id !== studentId));
    } else {
      setStudentsToAdd([...studentsToAdd, studentId]);
    }
  }
  function selectStudentToRemove(studentId) {
    if (studentsToRemove.includes(studentId)) {
      setStudentsToRemove(studentsToRemove.filter((id) => id !== studentId));
    } else {
      setStudentsToRemove([...studentsToRemove, studentId]);
    }
  }

  function refreshStudentLists() {
    mutate(singleFeeTypeUrl);
    if (payingStudentsUrl) mutate(payingStudentsUrl);
    if (nonPayingStudentsUrl) mutate(nonPayingStudentsUrl);
  }

  const {
    trigger: addStudents,
    isMutating: isAdding,
    error: addError,
  } = useCustomMutation(
    `daily-billing/fees/${feeType?.id}/add-student`,
    (url, { arg }) => postMethod(url, arg),
  );

  const {
    trigger: removeStudents,
    isMutating: isRemoving,
    error: removeError,
  } = useCustomMutation(
    `daily-billing/fees/${feeType?.id}/remove-student`,
    (url, { arg }) => postMethod(url, arg),
  );

  return (
    <PageLayout>
      {isFeeTyping ? (
        <div className="mt-20 fade-in">
          <Loading small={true} withOverlay={false} description="Loading..." />
        </div>
      ) : (
        <div className="fee-type-page">
          <Breadcrumbs
            items={[
              { label: 'Daily Fees', to: '/daily-fees' },
              { label: 'Fee Types', to: '/daily-fees/types' },
              { label: feeType?.name, to: `/daily-fees/types/${feeType?.id}` },
              { label: cls?.classDescription, active: true },
            ]}
          />
          <hr className="grey-hr mt-210 mb-10" />

          <h1 className="h-20">Select Students for {feeType?.name}</h1>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <p className="text-16 font-bold">{cls?.classDescription}</p>
              <p className="text-14">
                {payingTotal} student{payingTotal === 1 ? '' : 's'}
              </p>
            </div>
            <button
              className="clean-btn text-primary text-12 font-bold pointer"
              onClick={() => window.print()}
            >
              <Printer size="12" strokeWidth="5px" /> &nbsp; Print
            </button>
          </div>

          <PrintOutHead
            title={`Students - ${feeType?.name}`}
            headings={[
              { label: 'Class', value: cls?.classDescription },
              { label: 'Total', value: payingTotal },
            ]}
          />

          <Tabs className="mt-20">
            <Tab id="tab-1" label="Paying Students">
              {/* REMOVE STUDENTS  */}
              <PrintView>
                <div>
                  <Search
                    id="search-paying-students"
                    name="search-paying-students"
                    className="mt-10"
                    labelText=""
                    value={payingSearchTerm}
                    onChange={(e) => setPayingSearchTerm(e.target.value)}
                    placeHolderText="Search students..."
                  />
                  <div className="d-flex-space-between mt-10">
                    <p className="text-14">Students Under {feeType?.name}</p>
                    <p className="text-14">Total: {payingTotal}</p>
                  </div>

                  {removeError && (
                    <p className="text-14 mt-10 text-danger no-print">
                      {removeError?.message}
                    </p>
                  )}

                  {isPayingLoading ? (
                    <div className="d-flex-center mt-20">
                      <Loading withOverlay={false} small={true} />
                    </div>
                  ) : payingStudents?.length > 0 ? (
                    <>
                      <StudentsTable
                        students={payingStudents}
                        selectStudent={selectStudentToRemove}
                        selectedStudents={studentsToRemove}
                        setSelectedStudents={setStudentsToRemove}
                        idPrefix="remove"
                      />
                      <div className="no-print">
                        <Pagination
                          backwardText="Previous page"
                          forwardText="Next page"
                          page={payingPaginate.page}
                          pageNumberText="Page Number"
                          pageSize={payingPaginate.pageSize}
                          pageSizes={[10, 20, 30, 40, 50, 100]}
                          totalItems={payingTotal}
                          onChange={(value) => setPayingPaginate(value)}
                        />
                      </div>
                    </>
                  ) : (
                    <p className="text-14 mt-10 no-print">
                      No students are currently paying for {feeType?.name}.
                    </p>
                  )}

                  <div className="mt-40" style={{ display: 'flex' }}>
                    <div style={{ marginLeft: 'auto' }}>
                      <CustomButton
                        type="submit"
                        style={{ flex: '1' }}
                        classes={`primary font-bold`}
                        disabled={isRemoving || studentsToRemove.length === 0}
                        onClick={() => {
                          removeStudents(
                            {
                              classId: cls?.classId,
                              studentids: studentsToRemove,
                            },
                            {
                              onSuccess() {
                                refreshStudentLists();
                                setStudentsToRemove([]);
                              },
                            },
                          );
                        }}
                      >
                        {isRemoving ? (
                          <div className="d-flex-center">
                            <Loading withOverlay={false} small={true} />
                          </div>
                        ) : (
                          'Remove Students'
                        )}
                      </CustomButton>
                    </div>
                  </div>
                </div>
              </PrintView>
            </Tab>
            <Tab id="tab-4" label="Non-Paying Students">
              {/* ADD STUDENTS */}
              <PrintView>
                <div className="mt-10">
                  <Search
                    id="search-non-paying-students"
                    name="search-non-paying-students"
                    className="mt-10"
                    labelText=""
                    value={nonPayingSearchTerm}
                    onChange={(e) => setNonPayingSearchTerm(e.target.value)}
                    placeHolderText="Search students..."
                  />
                  <div className="d-flex-space-between mt-10">
                    <p className="text-14">
                      Students Not Under {feeType?.name}
                    </p>
                    <p className="text-14">Total: {nonPayingTotal}</p>
                  </div>
                  {isLoading ? (
                    <div className="d-flex-center mt-20">
                      <Loading withOverlay={false} small={true} />
                    </div>
                  ) : null}

                  {nonPayingStudents?.length > 0 ? (
                    <>
                      <StudentsTable
                        students={nonPayingStudents}
                        selectStudent={selectStudentToAdd}
                        selectedStudents={studentsToAdd}
                        setSelectedStudents={setStudentsToAdd}
                        idPrefix="add"
                      />
                      <div className="no-print">
                        <Pagination
                          backwardText="Previous page"
                          forwardText="Next page"
                          page={nonPayingPaginate.page}
                          pageNumberText="Page Number"
                          pageSize={nonPayingPaginate.pageSize}
                          pageSizes={[10, 20, 30, 40, 50, 100]}
                          totalItems={nonPayingTotal}
                          onChange={(value) => setNonPayingPaginate(value)}
                        />
                      </div>
                    </>
                  ) : (
                    !isClassing && (
                      <p className="text-14 mt-10">
                        All students are included or no matching search results.
                      </p>
                    )
                  )}

                  {addError && (
                    <p className="text-14 mt-10 text-danger no-print">
                      {addError?.message}
                    </p>
                  )}

                  <div className="mt-40" style={{ display: 'flex' }}>
                    <div style={{ marginLeft: 'auto' }}>
                      <CustomButton
                        type="submit"
                        style={{ flex: '1' }}
                        classes={`primary font-bold`}
                        disabled={isAdding || studentsToAdd.length === 0}
                        onClick={() => {
                          addStudents(
                            {
                              classId: cls?.classId,
                              studentids: studentsToAdd,
                            },
                            {
                              onSuccess() {
                                refreshStudentLists();
                                setStudentsToAdd([]);
                              },
                            },
                          );
                        }}
                      >
                        {isAdding ? (
                          <div className="d-flex-center">
                            <Loading withOverlay={false} small={true} />
                          </div>
                        ) : (
                          'Add Students'
                        )}
                      </CustomButton>
                    </div>
                  </div>
                </div>
              </PrintView>
            </Tab>
          </Tabs>
        </div>
      )}
    </PageLayout>
  );
}

function StudentsTable({
  students,
  selectStudent,
  selectedStudents,
  setSelectedStudents,
  idPrefix = '',
}) {
  return (
    <div className="table-container">
      <table className="bx--data-table table-white">
        <thead>
          <tr>
            <th className="no-print">
              <Checkbox
                id={`select-all-students-${idPrefix}`}
                name={`select-all-students-${idPrefix}`}
                labelText=""
                checked={
                  students.length > 0 &&
                  students.every((student) =>
                    selectedStudents.includes(student.id),
                  )
                }
                indeterminate={
                  selectedStudents.length > 0 &&
                  !students.every((student) =>
                    selectedStudents.includes(student.id),
                  )
                }
                onChange={() => {
                  const pageIds = students.map((student) => student.id);
                  const allSelected = pageIds.every((id) =>
                    selectedStudents.includes(id),
                  );
                  if (allSelected) {
                    setSelectedStudents(
                      selectedStudents.filter((id) => !pageIds.includes(id)),
                    );
                  } else {
                    setSelectedStudents([
                      ...new Set([...selectedStudents, ...pageIds]),
                    ]);
                  }
                }}
              />
            </th>
            <th>Student ID</th>
            <th>First Name</th>
            <th>Last Name</th>
            <th>Gender</th>
            <th>Class</th>
          </tr>
        </thead>
        <tbody>
          {students?.map((student) => (
            <tr key={student.id}>
              <td className="no-print">
                <Checkbox
                  id={`student-${student.id}`}
                  name={`student-${student.id}`}
                  labelText=""
                  checked={selectedStudents.includes(student.id)}
                  onChange={() => selectStudent(student.id)}
                />
              </td>
              <td>
                {student.schoolStudentId
                  ? student.schoolStudentId
                  : student.studentId}
              </td>
              <td>{student.firstName}</td>
              <td>{student.lastName}</td>
              <td>{student.gender}</td>
              <td>{student.classDescription}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
